import {
    FinancialStatementTypes,
    IAccount,
    IAccountMappingTemplateGroup,
    IFinStatements,
    IGroupingMasking,
    IReclassesAccounts,
    IReclassesAndAdjustmentExtension,
    IReclassificationTransaction,
    ItemType,
    ReclassificationPeriod,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';

/**
 * REV-20899
 * =========
 * Single, centralized routine that materializes the transactional
 * `IReclassificationTransaction[]` list into the legacy per-account
 * `openingReClass` / `closingReClass` fields on every account-like node of a
 * fin statement (top-level accounts, FLI accounts, SubFLI accounts, FLI
 * `reclassesAccounts` phantoms, and SubFLI `reclassesAccounts` phantoms).
 *
 * Why this exists
 * ---------------
 * Every downstream report (Cashflow, Notes, Appropriation of Profit, Equity
 * Movement Schedule, Fixed Assets Schedule, the Print Preview, account-proof
 * PDFs, etc.) consumes `openingReClass` / `closingReClass` per account when
 * computing displayed values. The transactional model stores reclasses in
 * `IGroupingMasking.reclassifications` and treats those legacy fields as
 * **derived**. This helper recomputes those derived fields from the source-of-
 * truth transaction list so the rest of the pipeline keeps working without
 * any per-consumer code change.
 *
 * Behaviour
 * ---------
 * - No-op when `groupingAndMasking.reclassificationsVersion !== 1` (so legacy
 *   ARs that haven't been migrated are left exactly as they are).
 * - Zeros every account-like node's `openingReClass` / `closingReClass`
 *   first, then walks the transaction list and writes deltas (`-amount` on the
 *   source leg, `+amount` on the target leg) via composite-key lookup
 *   (`accountNumber` + `name`, falling back to a unique `accountNumber` when
 *   the composite-key lookup yields zero matches).
 * - Operates **per fin statement**: each statement only sees transactions
 *   whose `statement` field matches its `IFinStatements.type`.
 * - Mutates the inputs in place. Callers that need to preserve the original
 *   data must deep-clone beforehand.
 */
export function applyReclassificationsToFinStatements(
    finStatements: IFinStatements[] | null | undefined,
    groupingAndMasking: IGroupingMasking | null | undefined,
): void {
    if (!Array.isArray(finStatements) || finStatements.length === 0) return;
    // Only the transactional model (v1) drives this. v0 / undefined → leave alone.
    if (groupingAndMasking?.reclassificationsVersion !== 1) return;

    const allTransactions = Array.isArray(groupingAndMasking.reclassifications)
        ? groupingAndMasking.reclassifications
        : [];

    for (const fs of finStatements) {
        if (fs == null) continue;
        const statement = statementForFinStatement(fs);
        applyToSingleStatement(fs, allTransactions, statement);
    }
}

/** Maps the legacy `FinancialStatementTypes` to the new `ReclassificationStatement` value. */
function statementForFinStatement(fs: IFinStatements): ReclassificationStatement {
    return fs.type === FinancialStatementTypes.INCOME
        ? ReclassificationStatement.INCOME_STATEMENT
        : ReclassificationStatement.BALANCE_SHEET;
}

function applyToSingleStatement(
    fs: IFinStatements,
    allTransactions: IReclassificationTransaction[],
    statement: ReclassificationStatement,
): void {
    const m = fs.templateAndAccountsMapping;
    if (m == null) return;

    // Step 1: zero every reclass field on every node (leaf and aggregate).
    // This guarantees that removing the last transaction propagates correctly.
    const leafNodes = collectAllAccountLikeNodes(fs);
    for (const n of leafNodes) {
        (n as IAccount).openingReClass = 0;
        (n as IAccount).closingReClass = 0;
    }
    if (m.template?.group != null) zeroAggregateReclasses(m.template.group);

    // Step 2: resolve the statement's transactions against the current
    // account graph by composite key (`accountNumber` + `name`) and apply
    // the delta to EVERY matched node.
    //
    // The same logical account is intentionally collected multiple times by
    // `collectAllAccountLikeNodes` — once from `m.accounts` (top-level
    // imported list) and once per location inside `m.template.group` (FLI
    // account, SubFLI account, FLI phantom reclasses accounts, etc.). After
    // the upstream `cloneDeep` they are distinct object instances even
    // though they share the same composite key. Dispatching by reference
    // (via `uniqueByIdentity` + `resolveRef`'s single-match rule) caused the
    // engine to detect them as "ambiguous" and silently drop the
    // transaction, so notes/super-table/etc. (which read off the template-
    // side nodes) saw zero reclasses for every transactional reclass.
    //
    // Building the index here by composite key and writing the delta to
    // every bucket member ensures every occurrence of the same logical
    // account receives the same `closingReClass` / `openingReClass`,
    // regardless of which collection it lives on. Truly ambiguous refs
    // (no match at all) still fall back to the unique-by-number rule
    // mirrored from `resolveRef`.
    const txns = allTransactions.filter((t) => t.statement === statement);
    if (txns.length > 0) {
        // Build two indexes:
        //   byUniqueId:     uniqueAccountId → [node] (for disambiguated duplicates)
        //   byCompositeKey: accountNumber|name → [nodes] (for normal 2-part key lookup)
        //   byNumber:       accountNumber → [nodes] (for the unique-number fallback)
        const byUniqueId = new Map<string, Array<IAccount | IReclassesAccounts>>();
        const byCompositeKey = new Map<string, Array<IAccount | IReclassesAccounts>>();
        const byNumber = new Map<string, Array<IAccount | IReclassesAccounts>>();
        for (const n of leafNodes) {
            if (n == null) continue;
            const accountNumber = (n as IAccount).accountNumber;
            if (accountNumber == null) continue;
            const name = (n as IAccount).name ?? '';
            const uniqueAccountId: string | undefined = (n as IAccount).uniqueAccountId;

            // Index by uniqueAccountId (only for disambiguated accounts).
            if (uniqueAccountId) {
                let idBucket = byUniqueId.get(uniqueAccountId);
                if (idBucket == null) {
                    idBucket = [];
                    byUniqueId.set(uniqueAccountId, idBucket);
                }
                idBucket.push(n);
            }

            // Index by 2-part composite key (used when uniqueAccountId is absent).
            const key = `${accountNumber}|${name}`;
            let bucket = byCompositeKey.get(key);
            if (bucket == null) {
                bucket = [];
                byCompositeKey.set(key, bucket);
            }
            bucket.push(n);

            let numberBucket = byNumber.get(accountNumber);
            if (numberBucket == null) {
                numberBucket = [];
                byNumber.set(accountNumber, numberBucket);
            }
            numberBucket.push(n);
        }

        const resolveTargets = (
            ref: { accountNumber: string; name: string; uniqueAccountId?: string },
        ): Array<IAccount | IReclassesAccounts> => {
            // Rule 1: UUID-first lookup for disambiguated duplicates.
            if (ref.uniqueAccountId) {
                const byId = byUniqueId.get(ref.uniqueAccountId);
                if (byId != null && byId.length > 0) return byId;
                // UUID not found in this statement — fall through for resilience.
            }

            // Rule 2: exact 2-part composite-key match.
            const exact = byCompositeKey.get(`${ref.accountNumber}|${ref.name ?? ''}`);
            if (exact != null && exact.length > 0) return exact;

            // Rule 3: unique-by-number fallback (handles renames after transaction
            // was created). Same semantics as resolveRef's second rule.
            const numberBucket = byNumber.get(ref.accountNumber);
            if (numberBucket != null && numberBucket.length > 0) {
                // Group the unique-by-number candidates by their distinct
                // composite keys. Only apply when exactly one distinct key
                // exists, otherwise the ref is genuinely ambiguous.
                const distinctKeys = new Set(
                    numberBucket.map((n) => `${(n as IAccount).accountNumber}|${(n as IAccount).name ?? ''}`),
                );
                if (distinctKeys.size === 1) return numberBucket;
            }
            return [];
        };

        for (const t of txns) {
            const field: 'closingReClass' | 'openingReClass' =
                t.period === ReclassificationPeriod.CURRENT_YEAR ? 'closingReClass' : 'openingReClass';
            const sources = resolveTargets(t.source);
            const targets = resolveTargets(t.target);
            for (const src of sources) {
                (src as IReclassesAndAdjustmentExtension)[field] =
                    ((src as IReclassesAndAdjustmentExtension)[field] ?? 0) - t.amount;
            }
            for (const tgt of targets) {
                (tgt as IReclassesAndAdjustmentExtension)[field] =
                    ((tgt as IReclassesAndAdjustmentExtension)[field] ?? 0) + t.amount;
            }
        }
    }

    // Step 3: bottom-up aggregate leaf values onto every parent node so
    // FLI / SubFLI / group / section totals expose `closingReClass` /
    // `openingReClass` for downstream readers (EMS helper, FlisMapper, …).
    if (m.template?.group != null) aggregateReclassesUpTree(m.template.group);
}

/** Zero `openingReClass` / `closingReClass` on every aggregate node (FLI, SubFLI, group, section). */
function zeroAggregateReclasses(group: IAccountMappingTemplateGroup): void {
    (group as IReclassesAndAdjustmentExtension).openingReClass = 0;
    (group as IReclassesAndAdjustmentExtension).closingReClass = 0;
    const items: unknown[] | null =
        (Array.isArray((group as unknown as { sortedItems?: unknown[] }).sortedItems)
            ? ((group as unknown as { sortedItems?: unknown[] }).sortedItems as unknown[])
            : null) ?? (Array.isArray(group.items) ? (group.items as unknown as unknown[]) : null);
    if (items) {
        for (const item of items) {
            if (item == null) continue;
            const typed = item as { type?: ItemType };
            if (typed.type === ItemType.FLI) zeroFliAggregate(item as Record<string, unknown>);
        }
    }
    if (Array.isArray(group.groups)) {
        for (const sub of group.groups) zeroAggregateReclasses(sub);
    }
}

function zeroFliAggregate(fli: Record<string, unknown>): void {
    (fli as unknown as IReclassesAndAdjustmentExtension).openingReClass = 0;
    (fli as unknown as IReclassesAndAdjustmentExtension).closingReClass = 0;
    const accounts = Array.isArray(fli['accounts']) ? (fli['accounts'] as Array<Record<string, unknown>>) : null;
    if (accounts) {
        for (const a of accounts) {
            if (a == null) continue;
            const t = (a as { type?: ItemType }).type;
            if (t === ItemType.SUB_FLI || t === ItemType.OF_THAT_ITEM) {
                (a as unknown as IReclassesAndAdjustmentExtension).openingReClass = 0;
                (a as unknown as IReclassesAndAdjustmentExtension).closingReClass = 0;
            }
        }
    }
}

/**
 * Bottom-up reclass aggregation. Returns the (CY, PY) reclass totals for the
 * group's subtree and as a side effect writes the aggregated values onto every
 * FLI / SubFLI / group / section node visited.
 */
function aggregateReclassesUpTree(group: IAccountMappingTemplateGroup): { cy: number; py: number } {
    let cy = 0;
    let py = 0;

    const items: unknown[] | null =
        (Array.isArray((group as unknown as { sortedItems?: unknown[] }).sortedItems)
            ? ((group as unknown as { sortedItems?: unknown[] }).sortedItems as unknown[])
            : null) ?? (Array.isArray(group.items) ? (group.items as unknown as unknown[]) : null);

    if (items) {
        for (const item of items) {
            if (item == null) continue;
            const typed = item as { type?: ItemType };
            if (typed.type === ItemType.FLI) {
                const fliTotal = aggregateReclassesForFli(item as Record<string, unknown>);
                cy += fliTotal.cy;
                py += fliTotal.py;
            }
            // Skip SUB_TOTAL/HEADER/EMPTY — they're derived from scope, not children.
        }
    }
    if (Array.isArray(group.groups)) {
        for (const sub of group.groups) {
            const subTotal = aggregateReclassesUpTree(sub);
            cy += subTotal.cy;
            py += subTotal.py;
        }
    }

    (group as IReclassesAndAdjustmentExtension).closingReClass = cy;
    (group as IReclassesAndAdjustmentExtension).openingReClass = py;
    return { cy, py };
}

function aggregateReclassesForFli(fli: Record<string, unknown>): { cy: number; py: number } {
    let cy = 0;
    let py = 0;

    const accounts = Array.isArray(fli['accounts']) ? (fli['accounts'] as Array<Record<string, unknown>>) : null;
    if (accounts) {
        for (const a of accounts) {
            if (a == null) continue;
            const t = (a as { type?: ItemType }).type;
            if (t === ItemType.SUB_FLI || t === ItemType.OF_THAT_ITEM) {
                const subTotal = aggregateReclassesForSubFli(a as Record<string, unknown>);
                cy += subTotal.cy;
                py += subTotal.py;
            } else if (t !== ItemType.HEADER && t !== ItemType.EMPTY) {
                // Leaf account already has its reclass values set in Step 2.
                cy += (a as unknown as IReclassesAndAdjustmentExtension).closingReClass ?? 0;
                py += (a as unknown as IReclassesAndAdjustmentExtension).openingReClass ?? 0;
            }
        }
    }
    // Synthetic phantom reclass-accounts attached to the FLI.
    const fliReclassesAccounts = Array.isArray(fli['reclassesAccounts'])
        ? (fli['reclassesAccounts'] as Array<IReclassesAccounts>)
        : null;
    if (fliReclassesAccounts) {
        for (const ra of fliReclassesAccounts) {
            if (ra == null) continue;
            cy += ra.closingReClass ?? 0;
            py += ra.openingReClass ?? 0;
        }
    }

    (fli as unknown as IReclassesAndAdjustmentExtension).closingReClass = cy;
    (fli as unknown as IReclassesAndAdjustmentExtension).openingReClass = py;
    return { cy, py };
}

function aggregateReclassesForSubFli(sub: Record<string, unknown>): { cy: number; py: number } {
    let cy = 0;
    let py = 0;

    const accounts = Array.isArray(sub['accounts']) ? (sub['accounts'] as Array<IAccount>) : null;
    if (accounts) {
        for (const a of accounts) {
            if (a == null) continue;
            cy += a.closingReClass ?? 0;
            py += a.openingReClass ?? 0;
        }
    }
    const subReclassesAccounts = Array.isArray(sub['reclassesAccounts'])
        ? (sub['reclassesAccounts'] as Array<IReclassesAccounts>)
        : null;
    if (subReclassesAccounts) {
        for (const ra of subReclassesAccounts) {
            if (ra == null) continue;
            cy += ra.closingReClass ?? 0;
            py += ra.openingReClass ?? 0;
        }
    }

    (sub as unknown as IReclassesAndAdjustmentExtension).closingReClass = cy;
    (sub as unknown as IReclassesAndAdjustmentExtension).openingReClass = py;
    return { cy, py };
}

/**
 * Walks the fin statement and returns every account-like node we want to
 * stamp with reclass values. The list is intentionally exhaustive — even
 * if the same logical account appears in two places (top-level array AND
 * inside an FLI), each occurrence gets the same value via composite-key
 * lookup, so duplicates are harmless.
 */
function collectAllAccountLikeNodes(fs: IFinStatements): Array<IAccount | IReclassesAccounts> {
    const nodes: Array<IAccount | IReclassesAccounts> = [];

    const m = fs.templateAndAccountsMapping;
    if (m == null) return nodes;

    // Top-level imported accounts.
    if (Array.isArray(m.accounts)) {
        for (const a of m.accounts) {
            if (a != null) nodes.push(a as IAccount);
        }
    }

    // Template-side: FLIs and SubFLIs both carry `accounts[]` and `reclassesAccounts[]`.
    if (m.template?.group != null) {
        walkGroup(m.template.group, nodes);
    }

    return nodes;
}

function walkGroup(group: IAccountMappingTemplateGroup, out: Array<IAccount | IReclassesAccounts>): void {
    // Both `sortedItems` (rendered list) and `items` (raw list) exist; prefer
    // sortedItems where populated. They reference the same underlying objects,
    // so either is fine — picking one avoids visiting twice.
    const items: unknown[] | null =
        (Array.isArray((group as unknown as { sortedItems?: unknown[] }).sortedItems)
            ? ((group as unknown as { sortedItems?: unknown[] }).sortedItems as unknown[])
            : null) ?? (Array.isArray(group.items) ? (group.items as unknown as unknown[]) : null);

    if (items) {
        for (const item of items) {
            if (item == null) continue;
            const typed = item as { type?: ItemType };
            if (typed.type === ItemType.FLI) {
                walkFli(item as Record<string, unknown>, out);
            }
        }
    }
    if (Array.isArray(group.groups)) {
        for (const sub of group.groups) walkGroup(sub, out);
    }
}

function walkFli(fli: Record<string, unknown>, out: Array<IAccount | IReclassesAccounts>): void {
    const accounts = Array.isArray(fli['accounts']) ? (fli['accounts'] as Array<Record<string, unknown>>) : null;
    if (accounts) {
        for (const a of accounts) {
            if (a == null) continue;
            const t = (a as { type?: ItemType }).type;
            if (t === ItemType.SUB_FLI || t === ItemType.OF_THAT_ITEM) {
                const subAccounts = Array.isArray(a['accounts']) ? (a['accounts'] as Array<IAccount>) : null;
                if (subAccounts) {
                    for (const sa of subAccounts) {
                        if (sa != null) out.push(sa);
                    }
                }
                const subReclassesAccounts = Array.isArray(a['reclassesAccounts'])
                    ? (a['reclassesAccounts'] as Array<IReclassesAccounts>)
                    : null;
                if (subReclassesAccounts) {
                    for (const ra of subReclassesAccounts) {
                        if (ra != null) out.push(ra);
                    }
                }
            } else if (t !== ItemType.HEADER && t !== ItemType.EMPTY) {
                // Plain leaf account (and any other non-header/empty/sub variant).
                out.push(a as unknown as IAccount);
            }
        }
    }
    const fliReclassesAccounts = Array.isArray(fli['reclassesAccounts'])
        ? (fli['reclassesAccounts'] as Array<IReclassesAccounts>)
        : null;
    if (fliReclassesAccounts) {
        for (const ra of fliReclassesAccounts) {
            if (ra != null) out.push(ra);
        }
    }
}

